import { trpc } from '../../lib/trpc';
import { Typography } from '../ui/Typography';
import { OutlineButton } from '../ui/Button';

const numberFmt = new Intl.NumberFormat('en-US');

interface TotalCardProps {
  label: string;
  value: number;
}

const TotalCard = ({ label, value }: TotalCardProps) => (
  <div className="flex flex-col gap-1 rounded-md border border-border-subtle bg-surface px-6 py-5">
    <Typography
      variant="caption"
      className="uppercase tracking-widest text-text-muted"
    >
      {label}
    </Typography>
    <span className="font-serif text-4xl font-normal leading-none text-text-primary">
      {numberFmt.format(value)}
    </span>
  </div>
);

interface CreatedDatePoint {
  date: string;
  count: number;
}

interface CreatedDateChartProps {
  data: CreatedDatePoint[];
}

const dateLabelFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

const dateTooltipFmt = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const formatDateLabel = (iso: string, fmt: Intl.DateTimeFormat): string => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return fmt.format(new Date(y, m - 1, d));
};

const CreatedDateChart = ({ data }: CreatedDateChartProps) => {
  if (data.length === 0) {
    return (
      <section className="overflow-hidden rounded-md border border-border-subtle bg-surface">
        <header className="border-b border-border-subtle bg-surface-secondary px-5 py-3">
          <Typography
            variant="heading4"
            className="font-serif font-normal text-text-primary"
            as="h3"
          >
            Wineries added over time
          </Typography>
        </header>
        <p className="px-5 py-6 text-center text-sm italic text-text-muted">
          No wineries in database
        </p>
      </section>
    );
  }

  const width = 960;
  const height = 280;
  const paddingLeft = 44;
  const paddingRight = 16;
  const paddingTop = 16;
  const paddingBottom = 36;
  const plotWidth = width - paddingLeft - paddingRight;
  const plotHeight = height - paddingTop - paddingBottom;

  const maxCount = data.reduce((m, p) => (p.count > m ? p.count : m), 0);
  const yMax = Math.max(1, maxCount);

  const barGap = 2;
  const barWidth = Math.max(2, plotWidth / data.length - barGap);
  const step = plotWidth / data.length;

  const tickIndices = (() => {
    if (data.length === 1) return [0];
    if (data.length === 2) return [0, 1];
    const mid = Math.floor((data.length - 1) / 2);
    return [0, mid, data.length - 1];
  })();

  return (
    <section className="overflow-hidden rounded-md border border-border-subtle bg-surface">
      <header className="border-b border-border-subtle bg-surface-secondary px-5 py-3">
        <Typography
          variant="heading4"
          className="font-serif font-normal text-text-primary"
          as="h3"
        >
          Wineries added over time
        </Typography>
      </header>
      <div className="px-5 py-5">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          role="img"
          aria-label="Wineries added per day"
          preserveAspectRatio="none"
          className="block h-64 w-full"
        >
          {/* Y-axis baseline + max gridline */}
          <line
            x1={paddingLeft}
            x2={width - paddingRight}
            y1={paddingTop + plotHeight}
            y2={paddingTop + plotHeight}
            stroke="#DBD4C9"
            strokeWidth={1}
          />
          <line
            x1={paddingLeft}
            x2={width - paddingRight}
            y1={paddingTop}
            y2={paddingTop}
            stroke="#DBD4C9"
            strokeWidth={1}
            strokeDasharray="2 4"
          />

          {/* Y-axis labels: 0 and max */}
          <text
            x={paddingLeft - 8}
            y={paddingTop + plotHeight}
            textAnchor="end"
            dominantBaseline="middle"
            className="fill-text-muted"
            fontSize={11}
            fontFamily="ui-sans-serif, system-ui, sans-serif"
          >
            0
          </text>
          <text
            x={paddingLeft - 8}
            y={paddingTop}
            textAnchor="end"
            dominantBaseline="middle"
            className="fill-text-muted"
            fontSize={11}
            fontFamily="ui-sans-serif, system-ui, sans-serif"
          >
            {numberFmt.format(yMax)}
          </text>

          {/* Bars */}
          {data.map((point, i) => {
            const barHeight = (point.count / yMax) * plotHeight;
            const x = paddingLeft + i * step + (step - barWidth) / 2;
            const y = paddingTop + plotHeight - barHeight;
            return (
              <rect
                key={point.date}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={1}
                fill="#4A5236"
              >
                <title>
                  {`${formatDateLabel(point.date, dateTooltipFmt)} — ${numberFmt.format(point.count)}`}
                </title>
              </rect>
            );
          })}

          {/* X-axis labels */}
          {tickIndices.map((idx) => {
            const x = paddingLeft + idx * step + step / 2;
            return (
              <text
                key={idx}
                x={x}
                y={height - paddingBottom / 2 + 4}
                textAnchor="middle"
                className="fill-text-muted"
                fontSize={11}
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {formatDateLabel(data[idx].date, dateLabelFmt)}
              </text>
            );
          })}
        </svg>
      </div>
    </section>
  );
};

interface StatTableProps {
  title: string;
  columns: string[];
  rows: Array<Array<string | number>>;
  emptyLabel: string;
}

const StatTable = ({ title, columns, rows, emptyLabel }: StatTableProps) => {
  const lastColIndex = columns.length - 1;

  return (
    <section className="overflow-hidden rounded-md border border-border-subtle bg-surface">
      <header className="border-b border-border-subtle bg-surface-secondary px-5 py-3">
        <Typography
          variant="heading4"
          className="font-serif font-normal text-text-primary"
          as="h3"
        >
          {title}
        </Typography>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-subtle text-left text-text-muted">
              {columns.map((col, i) => (
                <th
                  key={col}
                  className={`px-5 py-2 font-sans text-xs font-medium uppercase tracking-wider ${
                    i === lastColIndex ? 'text-right' : ''
                  }`}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-5 py-6 text-center italic text-text-muted"
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row, rIdx) => (
                <tr
                  key={rIdx}
                  className="border-b border-border-subtle last:border-b-0"
                >
                  {row.map((cell, cIdx) => {
                    const isLast = cIdx === lastColIndex;
                    const isNone = typeof cell === 'string' && cell === '(none)';
                    return (
                      <td
                        key={cIdx}
                        className={`px-5 py-2 text-text-primary ${
                          isLast
                            ? 'text-right font-mono tabular-nums'
                            : 'font-sans'
                        } ${isNone ? 'italic text-text-muted' : ''}`}
                      >
                        {typeof cell === 'number' ? numberFmt.format(cell) : cell}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const DatabaseStats = () => {
  const { data, isLoading, error, refetch, isFetching } =
    trpc.admin.stats.getDatabaseStats.useQuery();

  return (
    <section className="flex flex-col gap-6">
      <header className="flex items-center justify-between">
        <Typography
          variant="heading2"
          className="text-text-primary"
          as="h2"
        >
          Database Statistics
        </Typography>
        <OutlineButton
          size="sm"
          onClick={() => refetch()}
          loading={isFetching}
          disabled={isFetching}
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </OutlineButton>
      </header>

      {error ? (
        <div className="rounded-md border border-error-300 bg-error-50 px-5 py-4 text-sm text-error-700">
          Failed to load statistics: {error.message}
        </div>
      ) : null}

      {isLoading ? (
        <p className="font-sans text-sm text-text-muted">Loading…</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TotalCard label="Wineries" value={data.totals.wineries} />
            <TotalCard label="Wines" value={data.totals.wines} />
            <TotalCard label="Distinct varietals" value={data.totals.varietals} />
          </div>

          <CreatedDateChart data={data.byCreatedDate} />

          <StatTable
            title="Wineries by State / Province"
            columns={['Country', 'State / Province', 'Count']}
            rows={data.byState.map((r) => [r.country, r.state, r.count])}
            emptyLabel="No wineries in database"
          />

          <StatTable
            title="Wineries by City"
            columns={['Country', 'State / Province', 'City', 'Count']}
            rows={data.byCity.map((r) => [r.country, r.state, r.city, r.count])}
            emptyLabel="No wineries in database"
          />

          <StatTable
            title="Wines by Varietal"
            columns={['Varietal', 'Count']}
            rows={data.byVarietal.map((r) => [r.varietal, r.count])}
            emptyLabel="No wines in database"
          />
        </>
      ) : null}
    </section>
  );
};
